import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Activity, TrendingUp, Zap, Layers, Clock, Brain, BarChart3, MessageSquare, FileText, Shield, Users, Lightbulb, Target, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const STATUS_COLORS = { excellent: '#10b981', good: '#3b82f6', developing: '#f59e0b', starting: '#e0e0e0' };
const STATUS_NL = { excellent: 'Uitstekend', good: 'Goed', developing: 'Groeiend', starting: 'Beginnend' };
const STATUS_EN = { excellent: 'Excellent', good: 'Good', developing: 'Developing', starting: 'Starting' };

const TOOL_ICONS = {
  'cross-intelligence': Brain,
  'agent-assistant': MessageSquare,
  'sales': Target,
  'knowledge': FileText,
  'coach': Lightbulb,
  'risk': Shield,
  'hiring': Briefcase,
  'cashflow': BarChart3,
  'explorer': Lightbulb,
  'meeting': Users,
  'simulator': Activity,
  'automation': Zap,
};

const TOOL_NAMES_NL = {
  'cross-intelligence': 'Cross-Tool Intelligence',
  'agent-assistant': 'AI Assistent',
  'sales': 'Sales Assistent',
  'knowledge': 'Kennisbank',
  'coach': 'AI Coach',
  'risk': 'Risico Detector',
  'hiring': 'Hiring Assistent',
  'cashflow': 'Cashflow Predictor',
  'explorer': 'Insight Explorer',
  'meeting': 'Meeting Brain',
  'meeting_brain': 'Meeting Brain',
  'writing': 'Schrijfassistent',
  'simulator': 'Scenario Simulator',
  'automation': 'Automatisering',
};

function timeAgo(timestamp, lang) {
  if (!timestamp) return '';
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (lang === 'nl') {
    if (mins < 1) return 'zojuist';
    if (mins < 60) return `${mins} min geleden`;
    if (hours < 24) return `${hours}u geleden`;
    if (days === 1) return 'gisteren';
    return `${days}d geleden`;
  }
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

const HealthMonitor = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/health-monitor/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(console.error);
  }, [token]);

  if (!data) return null;

  const color = STATUS_COLORS[data.status] || '#e0e0e0';
  const statusLabel = lang === 'nl' ? STATUS_NL[data.status] : STATUS_EN[data.status];
  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (data.overall_health / 100) * circumference;
  const recent = data.recent_activity || [];

  return (
    <div className="health-card" data-testid="health-monitor">
      <div className="readiness-header">
        <Activity size={20} />
        <h3 className="heading-3">{lang === 'nl' ? 'Platform Activiteit' : 'Platform Activity'}</h3>
      </div>
      <div className="readiness-body">
        <div className="readiness-ring-wrap">
          <svg width="110" height="110" viewBox="0 0 110 110">
            <circle cx="55" cy="55" r="42" fill="none" stroke="var(--border-light)" strokeWidth="8" />
            <circle cx="55" cy="55" r="42" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={offset} transform="rotate(-90 55 55)"
              style={{ transition: 'stroke-dashoffset 1s ease' }} />
          </svg>
          <div className="readiness-ring-text">
            <span className="readiness-pct">{data.overall_health}</span>
            <span className="readiness-level" style={{ color }}>{statusLabel}</span>
          </div>
        </div>
        <div className="readiness-stats">
          <div className="readiness-stat"><Zap size={16} /><div><span className="readiness-stat-val">{data.metrics.engagement.score}%</span><span className="readiness-stat-lbl">{lang === 'nl' ? 'Betrokkenheid' : 'Engagement'}</span></div></div>
          <div className="readiness-stat"><Layers size={16} /><div><span className="readiness-stat-val">{data.metrics.diversity.tools_used}/{data.metrics.diversity.total_tools}</span><span className="readiness-stat-lbl">{lang === 'nl' ? 'Diversiteit' : 'Diversity'}</span></div></div>
          <div className="readiness-stat"><TrendingUp size={16} /><div><span className="readiness-stat-val">{data.metrics.depth.score}%</span><span className="readiness-stat-lbl">{lang === 'nl' ? 'Diepgang' : 'Depth'}</span></div></div>
        </div>
      </div>

      {/* Recent Activity */}
      {recent.length > 0 && (
        <div data-testid="recent-activity" style={{
          marginTop: '1rem', paddingTop: '0.85rem',
          borderTop: '1px solid var(--border-light)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.6rem' }}>
            <Clock size={13} style={{ color: 'var(--text-secondary)' }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.03em' }}>
              {lang === 'nl' ? 'RECENTE ACTIVITEIT' : 'RECENT ACTIVITY'}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(Array.isArray(recent) ? recent : []).map((item, i) => {
              const Icon = TOOL_ICONS[item.tool_id] || Activity;
              const name = lang === 'nl'
                ? (TOOL_NAMES_NL[item.tool_id] || (typeof item.tool_name === 'string' ? item.tool_name : item.tool_id))
                : (typeof item.tool_name === 'string' ? item.tool_name : item.tool_id);
              return (
                <div key={i} data-testid={`activity-${i}`} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '0.45rem 0.6rem',
                  background: 'rgba(255,255,255,0.02)',
                  borderRadius: 8,
                  transition: 'background 0.15s',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: 'var(--accent-blue-200, rgba(59,130,246,0.1))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={14} style={{ color: 'var(--accent-blue, #3b82f6)' }} />
                  </div>
                  <span style={{ flex: 1, fontSize: '0.82rem', fontWeight: 500 }}>{name}</span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {timeAgo(item.timestamp, lang)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthMonitor;
